import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { List, Card, Pagination, Typography, Result, Button, Space, Tag, Empty, Input } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import PageHeader from '../Layout/PageHeader';
import { tns } from '../../i18n/common';

const { Title, Text } = Typography;

const VideosGrid = () => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [size, setSize] = useState(12);
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');
    const [categoryDefs, setCategoryDefs] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const isAdmin = () => {
        const rr = (user?.azure_roles || []).map(r => String(r).toLowerCase());
        const orr = (user?.azure_organization_roles || []).map(r => String(r).toLowerCase());
        return rr.includes('admin') || orr.includes('admin');
    };

    const fetchData = async (p = 1) => {
        setLoading(true);
        try {
            const [u, res] = await Promise.all([
                axios.post('/user').catch(() => ({ data: null })),
                axios.get(`/v1/videos`, { params: { page: p, size, category, language: locale, search } })
            ]);
            setUser(u?.data || null);
            setItems(res.data?.data || []);
            setTotal(res.data?.pagination?.total || 0);
            setPage(res.data?.pagination?.page || p);
            setSize(res.data?.pagination?.size || size);
        } catch (e) {
            console.error('Failed to load videos', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, locale, search]);

    useEffect(() => {
        const onLocale = () => setLocale(localStorage.getItem('locale') || 'en');
        window.addEventListener('locale-changed', onLocale);
        return () => window.removeEventListener('locale-changed', onLocale);
    }, []);

    // Mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadCats = async () => {
            try {
                const res = await axios.get('/v1/settings/video-categories');
                setCategoryDefs(res.data?.data || []);
            } catch {}
        };
        loadCats();
    }, [locale]);

    const categoryLabel = (key) => {
        if (!key) return '';
        const def = categoryDefs.find(c => c.key === key);
        if (!def) return key;
        return def.labels?.[locale] || def.labels?.en || key;
    };

    const categories = (categoryDefs.length
        ? categoryDefs.map(c => ({ value: c.key, label: categoryLabel(c.key) }))
        : Array.from(new Set(items.map(i => i.category).filter(Boolean))).map(c => ({ value: c, label: c }))
    );
    const navItems = [{ key: '', label: tns('videos', 'allCategories') }, ...categories.map(c => ({ key: c.value, label: c.label }))];

    return (
                <div style={{
            backgroundColor: '#fafafa',
            minHeight: '100vh'
        }}>
            <PageHeader
                title={tns('videos', 'title')}
                subtitle={tns('videos', 'subtitle')}
                actions={isAdmin() && (
                    <Link to="/admin/videos/new">
                        <Button type="primary" icon={<PlusOutlined />} size="large">
                            {tns('videos', 'newVideo')}
                        </Button>
                    </Link>
                )}
            >
                {/* Category Navigation and Search */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 16 : 24,
                    alignItems: isMobile ? 'stretch' : 'center'
                }}>
                    {/* Category Navigation */}
                    <div className="videos-cat-nav" style={{
                        display: 'flex',
                        gap: 8,
                        overflowX: 'auto',
                        padding: '2px 0',
                        scrollbarWidth: 'thin',
                        flex: isMobile ? 'none' : 1
                    }}>
                        {navItems.map(item => (
                            <button
                                key={item.key || 'all'}
                                onClick={() => setCategory(item.key)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: 999,
                                    border: category === item.key ? '1px solid var(--color-primary, #1677ff)' : '1px solid #e6e6e6',
                                    background: category === item.key ? 'var(--color-primary, #1677ff)' : '#fff',
                                    color: category === item.key ? '#fff' : '#555',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    fontSize: 14,
                                    fontWeight: category === item.key ? 600 : 400,
                                    transition: 'all 0.2s ease',
                                    boxShadow: category === item.key ? '0 2px 4px rgba(22,119,255,0.2)' : 'none'
                                }}
                                aria-pressed={category === item.key}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* Search Input */}
                    <div style={{
                        width: isMobile ? '100%' : '300px',
                        flexShrink: 0
                    }}>
                        <Input.Search
                            placeholder={tns('videos', 'searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onSearch={() => fetchData(1)}
                            allowClear
                            size="middle"
                            prefix={<SearchOutlined />}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
            </PageHeader>

            {/* Content Section */}
            <div style={{ padding: '40px 24px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    {(!loading && items.length === 0) ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px'
                        }}>
                            <Empty
                                description={
                                    <div style={{ textAlign: 'center' }}>
                                        <Title level={4} style={{ color: '#8c8c8c' }}>{tns('videos', 'noVideosYet')}</Title>
                                        <Text type="secondary">
                                            {tns('videos', 'noVideosDescription')}
                                        </Text>
                                    </div>
                                }
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            >
                                {isAdmin() && (
                                    <Link to="/admin/videos/new">
                                        <Button type="primary" icon={<PlusOutlined />}>
                                            {tns('videos', 'addFirstVideo')}
                                        </Button>
                                    </Link>
                                )}
                            </Empty>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                                {items.map(item => (
                                    <Link key={item.id} to={`/videos/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <Card
                                            hoverable
                                            style={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                                border: '1px solid #f0f0f0',
                                                transition: 'all 0.3s ease'
                                            }}
                                            cover={
                                                <img
                                                    src={item.thumbnail_url || '/images/placeholder/video-placeholder.jpg'}
                                                    alt={item.title}
                                                    style={{ height: 160, objectFit: 'cover' }}
                                                />
                                            }
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', marginBottom: 8 }}>
                                                <span style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    display: '-webkit-box',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 3,
                                                    lineHeight: 1.3,
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: '#1a1a1a'
                                                }}>
                                                    {item.title}
                                                </span>
                                                {item.created_at && (new Date(item.created_at) > new Date(Date.now() - 7*24*60*60*1000)) ? (
                                                    <Tag color="green" style={{ margin: 0, fontSize: 10 }}>New</Tag>
                                                ) : null}
                                            </div>
                                            {item.category ? (
                                                <Tag color="blue" style={{ margin: 0, fontSize: 10, alignSelf: 'flex-start' }}>
                                                    {categoryLabel(item.category)}
                                                </Tag>
                                            ) : null}
                                            <div style={{ marginTop: 'auto' }} />
                                        </Card>
                                    </Link>
                                ))}
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: 32,
                                padding: '24px 0'
                            }}>
                                <Pagination
                                    current={page}
                                    pageSize={size}
                                    total={total}
                                    onChange={(p) => fetchData(p)}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} videos`}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VideosGrid;
